import { useState, useEffect } from 'react';
import { Mail, Send, CheckCircle, Clock, XCircle, Copy, Trash2, Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase, Invitation } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import DeleteConfirmationModal from './DeleteConfirmationModal';

export default function InvitationManager() {
  const { t } = useTranslation();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [invitationToDelete, setInvitationToDelete] = useState<Invitation | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    const { data, error } = await supabase
      .from('invitations')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setInvitations(data);
    }
  };

  const generateToken = () => {
    return Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = generateToken();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const { error: insertError } = await supabase
        .from('invitations')
        .insert({
          email,
          token,
          created_by: user?.id,
          expires_at: expiresAt.toISOString(),
          status: 'pending'
        });

      if (insertError) throw insertError;

      const invitationUrl = `${window.location.origin}?invitation=${token}`;
      setSuccess(`${t('invitations.invitationCreatedMessage')} ${invitationUrl}`);
      setEmail('');
      loadInvitations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="w-5 h-5 text-emerald-600" />;
      case 'expired':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-amber-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-emerald-100 text-emerald-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-amber-100 text-amber-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'accepted':
        return t('invitations.accepted');
      case 'expired':
        return t('invitations.expired');
      default:
        return t('invitations.pending');
    }
  };

  const getInvitationUrl = (token: string) => {
    return `${window.location.origin}?invitation=${token}`;
  };

  const handleCopyUrl = async (invitation: Invitation) => {
    const url = getInvitationUrl(invitation.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(invitation.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      setError(t('invitations.copyError'));
    }
  };

  const handleDeleteClick = (invitation: Invitation) => {
    setInvitationToDelete(invitation);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!invitationToDelete) return;

    try {
      const { error: deleteError } = await supabase
        .from('invitations')
        .delete()
        .eq('id', invitationToDelete.id);

      if (deleteError) throw deleteError;

      setSuccess(t('invitations.deleteSuccess'));
      setDeleteModalOpen(false);
      setInvitationToDelete(null);
      loadInvitations();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('invitations.deleteError'));
      setDeleteModalOpen(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800">{t('invitations.sendInvitationTitle')}</h2>
        </div>

        {error && (
          <div className="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 sm:p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-800 font-medium mb-2">{t('invitations.invitationCreated')}</p>
            <p className="text-sm text-emerald-700 break-all">{success}</p>
          </div>
        )}

        <form onSubmit={handleSendInvitation} className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('invitations.emailPlaceholder')}
            className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
            required
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <Send className="w-4 h-4" />
            <span>{loading ? t('invitations.sending') : t('invitations.sendInvitation')}</span>
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">{t('invitations.recentInvitations')}</h3>

        <div className="space-y-3">
          {invitations.length === 0 ? (
            <p className="text-slate-500 text-center py-8">{t('invitations.noInvitations')}</p>
          ) : (
            invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex flex-col gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                    {getStatusIcon(invitation.status)}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-800 truncate">{invitation.email}</p>
                      <p className="text-sm text-slate-500">
                        {t('invitations.sent')} {new Date(invitation.created_at).toLocaleDateString()}
                        {' • '}
                        {t('invitations.expires')} {new Date(invitation.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusColor(invitation.status)}`}>
                    {getStatusText(invitation.status)}
                  </span>
                </div>

                {invitation.status === 'pending' && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg border border-slate-200">
                      <Link2 className="w-4 h-4 text-slate-500 flex-shrink-0" />
                      <input
                        type="text"
                        value={getInvitationUrl(invitation.token)}
                        readOnly
                        className="flex-1 bg-transparent text-xs text-slate-600 outline-none truncate"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCopyUrl(invitation)}
                        className="flex-1 sm:flex-none px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Copy className="w-4 h-4" />
                        <span>{copiedId === invitation.id ? t('invitations.copied') : t('invitations.copyLink')}</span>
                      </button>
                      <button
                        onClick={() => handleDeleteClick(invitation)}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">{t('invitations.delete')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('invitations.deleteInvitation')}
        message={t('invitations.deleteInvitationMessage')}
      />
    </div>
  );
}
